import { Upload, X } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";

import { DropzoneOverlay } from "@/components/dropzone-overlay";
import { OptimizedImage } from "@/components/optimized-image";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/lib/utils";
import { useUploadFile } from "@/hooks/use-upload-file";

import { useFieldContext } from "./form-context";

type ImageUploadProps = {
	className?: string;
	variant?: "default" | "avatar";
};

export function ImageUpload({ className, variant = "default" }: ImageUploadProps) {
	const field = useFieldContext<string>();

	const value = field.store.state.value;

	const {
		file,
		resetFile,
		mutate: uploadFile,
	} = useUploadFile({
		onSuccess: (file) => {
			if (file.url) {
				field.handleChange(file.url);
			}
		},
	});

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			const selectedFile = acceptedFiles[0];
			if (!selectedFile) return;
			uploadFile(selectedFile);
		},
		[uploadFile],
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			"image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"],
		},
		multiple: false,
	});

	// Sync local file state with form value if needed, or if form value changes externally
	useEffect(() => {
		if (value && !file?.fileUrl) {
			// If we have a value but no file object (initial load), we don't need to do anything
			// the img src will use the value directly.
		}
		if (!value && file) {
			resetFile();
		}
	}, [value, file, resetFile]);

	const handleRemoveImage = (e: React.MouseEvent) => {
		e.stopPropagation();
		resetFile();
		field.handleChange("");
	};

	const isAvatar = variant === "avatar";

	return (
		<div className="space-y-2">
			<div {...getRootProps()} className="relative">
				<input {...getInputProps()} />
				{isDragActive && <DropzoneOverlay size="sm" />}
				{value ? (
					<div
						className={cn(
							"relative aspect-square mx-auto overflow-hidden border-2 border-dashed border-muted-foreground/25 cursor-pointer hover:border-primary transition-colors",
							isAvatar
								? "w-24 h-24 max-w-24 rounded-full"
								: "w-full max-w-[200px] rounded-lg",
							className,
						)}
					>
						<OptimizedImage
							src={value}
							alt=""
							layout="constrained"
							width={isAvatar ? 96 : 200}
							height={isAvatar ? 96 : 200}
							className="w-full h-full object-cover"
						/>
						<Button
							type="button"
							variant="secondary"
							size="icon-sm"
							className={cn(
								"absolute",
								isAvatar
									? "-top-1 -right-1 size-5 bg-background/90"
									: "top-2 right-2 size-6",
							)}
							onClick={handleRemoveImage}
						>
							<X className={cn(isAvatar ? "h-3 w-3" : "h-4 w-4")} />
						</Button>
					</div>
				) : (
					<div
						className={cn(
							"flex items-center justify-center aspect-square mx-auto border-2 border-dashed border-muted-foreground/25 bg-muted/5 hover:bg-muted/10 hover:border-primary transition-colors cursor-pointer",
							isAvatar
								? "w-24 h-24 max-w-24 rounded-full"
								: "w-full max-w-[200px] rounded-lg",
							isDragActive && "border-primary bg-primary/5",
							className,
						)}
					>
						<div
							className={cn(
								"flex flex-col items-center",
								isAvatar ? "gap-1 p-2" : "gap-2 p-6",
							)}
						>
							<Upload
								className={cn(
									"text-muted-foreground",
									isAvatar ? "h-5 w-5" : "h-8 w-8",
								)}
							/>
							{!isAvatar && (
								<span className="text-sm text-muted-foreground text-center">
									拖拽图片到此处
									<br />
									或点击上传
								</span>
							)}
						</div>
					</div>
				)}
			</div>
			{(file?.status === "uploading" || file?.status === "processing") && (
				<p className="text-sm text-muted-foreground text-center">
					上传中... {file.uploadState?.progress || 0}%
				</p>
			)}
		</div>
	);
}
